<template>
  <div>
    <AppNavbar>
      <button class="create-account-btn" @click="handleCreateAccountClick">
        <span class="btn-emoji">🆕</span> Create account
      </button>
      <!-- ➕, ⭐, 🚀, 🆕  -->
      <span class="nav-or-text">or</span>
      <GoogleSignInButton />
    </AppNavbar>
    <div class="spacer"></div>

    <section class="hero">
      <div class="hero-content">
        <div class="hero-logo-container">
          <img src="logo.png" alt="InboxMaster Logo" />
          <h1 class="hero-title">inboxmaster.ai</h1>
        </div>
        <p>
          A smarter way to manage your emails.<br />
          Our AI analyzes and filters your inbox so you can focus on what truly
          matters.
        </p>
      </div>
    </section>

    <section id="about">
      <div class="container">
        <h2>About <span style="font-weight: 100">inboxmaster.ai</span></h2>
        <div class="about-content">
          <div class="about-text">
            <p>
              inboxmaster.ai is a next‑generation email management platform
              designed to provide intelligent assistance by analyzing your
              messages before you even see them.
            </p>
            <br />
            <p>
              Our advanced spam filtering and AI‑driven insights help streamline
              your communication, reduce clutter, and prioritize what's
              important.
            </p>
            <br />
            <p>
              Whether you're an executive or simply overwhelmed by your inbox,
              inboxmaster.ai is engineered to save you time and boost
              productivity.
            </p>
          </div>
          <div class="about-divider"></div>
          <AboutFeatures />
        </div>
      </div>
    </section>

    <section id="plans">
      <div class="container">
        <h2>Our Plans</h2>
        <PricingPlans />
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import PricingPlans from "@/components/PricingPlans.vue";
import AppFooter from "@/components/AppFooter.vue";
import AppNavbar from "@/components/AppNavbar.vue";
import AboutFeatures from "@/components/AboutFeatures.vue";
import GoogleSignInButton from "@/components/GoogleSignInButton.vue";
import { trackAndFlush } from "@/analytics/mixpanel";

export default defineComponent({
  name: "LandingPage",
  components: {
    GoogleSignInButton,
    PricingPlans,
    AppFooter,
    AppNavbar,
    AboutFeatures,
  },
  methods: {
    handleCreateAccountClick() {
      trackAndFlush("Landing Create Account Clicked");
      this.$router.push("/plans");
    },
  },
});
</script>

<style>
:root {
  --primary-color: #ba7ba1;
  --primary-color-dark: #a66d8e;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Inter", Avenir, Helvetica, Arial, sans-serif;
  font-weight: 100;
  color: #333;
  line-height: 1.6;
  background-color: #f7f7f7;
}

header {
  background-color: rgba(255, 255, 255, 0.5);
  padding: 20px 40px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.signin-section {
  margin-top: 3rem;
}

.container {
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
}

nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo-container {
  display: flex;
  align-items: center;
}

.logo-container img {
  height: 40px;
  margin-right: 10px;
}

.logo-text {
  font-size: 24px;
  font-weight: 100;
  color: black;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-links a {
  margin-left: 0;
  text-decoration: none;
  color: #333;
}

.create-account-btn {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-appearance: none;
  background-color: WHITE;
  background-image: none;
  border: 1px solid rgba(0, 0, 0, 0.3);
  -webkit-border-radius: 4px;
  border-radius: 4px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  color: #1f1f1f;
  cursor: pointer;
  font-size: 14px;
  height: 40px;
  letter-spacing: 0.25px;
  outline: none;
  overflow: hidden;
  padding: 0 12px;
  position: relative;
  text-align: center;
  -webkit-transition: background-color 0.218s, border-color 0.218s,
    box-shadow 0.218s;
  transition: background-color 0.218s, border-color 0.218s, box-shadow 0.218s;
  vertical-align: middle;
  white-space: nowrap;
  width: auto;
  max-width: 400px;
  min-width: min-content;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.create-account-btn:not(:disabled):hover {
  -webkit-box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
  box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.create-account-btn::before {
  content: "";
  -webkit-transition: opacity 0.218s;
  transition: opacity 0.218s;
  bottom: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  background-color: #303030;
  pointer-events: none;
}

.create-account-btn:not(:disabled):hover::before {
  opacity: 8%;
}

.create-account-btn:not(:disabled):active::before,
.create-account-btn:not(:disabled):focus::before {
  opacity: 12%;
}

.create-account-btn:disabled {
  cursor: default;
  background-color: #ffffff61;
  border-color: #1f1f1f1f;
}

.btn-emoji {
  font-size: 20px;
  margin-right: 10px;
  display: inline-block;
  vertical-align: middle;
}

.nav-or-text {
  color: #666;
  font-size: 14px;
  font-weight: 400;
  margin: 0 8px;
}

.hero {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 20px;
  position: relative;
  padding-bottom: 2rem;
  padding-top: 2rem;
}

.hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.hero-content {
  position: relative;
  z-index: 1;
  color: black;
  max-width: 700px;
}

.hero-content h1 {
  font-size: 48px;
  margin-bottom: 20px;
}

.hero-logo-container {
  flex-direction: column;
  align-items: center;
  display: flex;
  justify-content: center;
}

.hero-logo-container img {
  height: 150px;
  margin-right: 20px;
}

.hero-logo-container .hero-title {
  font-size: 72px;
  font-weight: 100;
  color: black;
  margin-bottom: -20px;
}

.hero-content p {
  font-size: 20px;
  margin-bottom: 10px;
}

.btn {
  display: inline-block;
  padding: 0.75em 1.5em;
  border: 0px solid black;
  background-color: white;
  color: black;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.25s ease, color 0.25s ease;
  min-width: 150px;
}

.btn:hover,
.btn:focus {
  outline: none;
  transform: translateY(-2px);
}

.btn:active {
  transform: scale(0.98);
}

section {
  padding: 50px 0;
}

#about,
#plans {
  background-color: #fff;
}

h2 {
  text-align: center;
  margin-bottom: 40px;
  font-size: 36px;
  color: black;
}

.content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
}

html {
  scroll-behavior: smooth;
}

.spacer {
  height: 80px;
}

.hero-title {
  color: black;
}

.about-content {
  display: flex;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 0;
  gap: 40px;
  align-items: flex-start;
}

.about-text {
  flex: 1;
  text-align: left;
}

.about-divider {
  width: 1px;
  background-color: #e0e0e0;
  align-self: stretch;
  margin: 20px 0;
}

.about-features {
  flex: 1;
  text-align: left;
}

.about-features ul {
  list-style: none;
  padding: 0;
}

.about-features li {
  margin-bottom: 15px;
  padding-left: 30px;
  position: relative;
}

.about-features li::before {
  content: "•";
  color: var(--primary-color);
  font-size: 1.5em;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

@media (max-width: 768px) {
  .create-account-btn {
    width: 100%;
    max-width: none;
    font-size: 16px;
    height: 44px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .btn-emoji {
    font-size: 18px;
    margin-right: 0;
    display: flex;
    align-items: center;
  }

  .nav-or-text {
    display: none;
  }

  /* Hero Section */
  .hero {
    padding: 1rem 15px;
    min-height: auto;
  }

  .hero-content {
    max-width: 100%;
    padding: 0 10px;
  }

  .hero-logo-container img {
    height: 100px;
    margin-right: 0;
    margin-bottom: 15px;
  }

  .hero-logo-container .hero-title {
    font-size: 48px;
    margin-bottom: 10px;
    line-height: 1.1;
  }

  .hero-content h1 {
    font-size: 32px;
    margin-bottom: 15px;
  }

  .hero-content p {
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  /* Spacer adjustment - remove spacer since header is no longer fixed */
  .spacer {
    height: 0;
  }

  /* Sections */
  section {
    padding: 30px 0;
  }

  .container {
    width: 95%;
    padding: 0 15px;
  }

  h2 {
    font-size: 28px;
    margin-bottom: 25px;
    line-height: 1.2;
  }

  /* About Section */
  .about-content {
    flex-direction: column;
    gap: 30px;
    padding: 0 10px;
  }

  .about-text p {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 15px;
  }

  .about-divider {
    width: 100%;
    height: 1px;
    margin: 0;
    align-self: center;
  }

  /* Features adjustments for mobile */
  .about-features li {
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 12px;
    padding-left: 25px;
  }

  .about-features li::before {
    font-size: 1.2em;
  }
}

/* Additional mobile optimizations for smaller screens */
@media (max-width: 480px) {
  header {
    padding: 12px 15px;
  }

  .hero-logo-container .hero-title {
    font-size: 36px;
  }

  .hero-content p {
    font-size: 16px;
  }

  .create-account-btn {
    font-size: 15px;
    height: 42px;
  }

  h2 {
    font-size: 24px;
  }

  .about-text p {
    font-size: 15px;
  }

  .container {
    width: 100%;
    padding: 0 10px;
  }
}
</style>
